// AI Security Command Center API client + types (mirrors services/firewall/src/services/riskScore.ts)

export type RiskSeverity = 'none' | 'low' | 'medium' | 'high' | 'critical';
export type RiskCategory = 'injection' | 'dlp' | 'denial' | 'rate_limit' | 'behaviour' | 'shadow' | 'other';

export interface AgentSignals {
  injections: number;
  dlp: number;
  denials: number;
  rateLimits: number;
  maxDrift: number;
  shadowHits: number;
}

export interface AgentRisk {
  agentId: string;
  name: string;
  status: string;
  score: number;
  severity: RiskSeverity;
  signals: AgentSignals;
  contributions: Record<string, number>;
  lastEvent: string | null;
}

export interface RiskEvent {
  timestamp: string;
  agentId: string;
  category: RiskCategory;
  label: string;
  detail: string | null;
  points: number;
}

export interface OrgRiskSummary {
  orgId: string;
  windowHours: number;
  topScore: number;
  topSeverity: RiskSeverity;
  agentsAtRisk: number;
  totals: { injections: number; dlp: number; denials: number; rateLimits: number; shadow: number; anomalies: number };
  topAgents: AgentRisk[];
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

export interface AiInventory {
  windowHours: number;
  apps: { total: number; unsanctioned: number };
  agents: { registered: number; shadow: number };
  mcp: { total: number; gateway: number; registered: number; unprotected: number; unprotectedHosts: string[] };
  users: number;
  communications: number;
  bytesIn: number;
  bytesOut: number;
  violations: number;
  topApps: Array<{ endpoint: string; provider: string | null; type: string; users: number; hits: number; bytesIn: number; bytesOut: number; managed: boolean }>;
}

export function getAiInventory(apiUrl: string, windowHours = 24): Promise<AiInventory> {
  return getJson(`${apiUrl}/api/admin/risk/inventory?window=${windowHours}`);
}

export function getRiskSummary(apiUrl: string, windowHours = 24): Promise<OrgRiskSummary> {
  return getJson(`${apiUrl}/api/admin/risk/summary?window=${windowHours}`);
}

export async function getAgentRisks(apiUrl: string, windowHours = 24): Promise<AgentRisk[]> {
  const data = await getJson<{ agents: AgentRisk[] }>(`${apiUrl}/api/admin/risk/agents?window=${windowHours}`);
  return data.agents;
}

export async function getRiskEvents(apiUrl: string, limit = 40): Promise<RiskEvent[]> {
  const data = await getJson<{ events: RiskEvent[] }>(`${apiUrl}/api/admin/risk/events?limit=${limit}`);
  return data.events;
}

export async function getAgentTimeline(apiUrl: string, agentId: string, windowHours = 24): Promise<RiskEvent[]> {
  const data = await getJson<{ events: RiskEvent[] }>(
    `${apiUrl}/api/admin/risk/agents/${encodeURIComponent(agentId)}/timeline?window=${windowHours}`
  );
  return data.events;
}

// ── Containment ───────────────────────────────────────────────────────────────
export interface QuarantineResult { agentId: string; status: string; quarantined: boolean }

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.message || b.error || `Request failed (${res.status})`);
  }
  return res.json();
}

export function quarantineAgent(apiUrl: string, agentId: string, reason?: string): Promise<QuarantineResult> {
  return postJson(`${apiUrl}/api/admin/risk/agents/${encodeURIComponent(agentId)}/quarantine`, { reason });
}

export function releaseAgent(apiUrl: string, agentId: string): Promise<QuarantineResult> {
  return postJson(`${apiUrl}/api/admin/risk/agents/${encodeURIComponent(agentId)}/release`);
}

// ── Attack Theater (demo mode) ────────────────────────────────────────────────
export type TheaterBeat = 'injection' | 'dlp' | 'shadow' | 'drift' | 'quarantine';
export interface TheaterState { risk: AgentRisk | null; status: string }
export interface TheaterBeatResult extends TheaterState { ok: boolean; beat: TheaterBeat }

export async function getDemoConfig(apiUrl: string): Promise<boolean> {
  try {
    const d = await getJson<{ enabled: boolean }>(`${apiUrl}/api/admin/demo/config`);
    return !!d.enabled;
  } catch {
    return false;
  }
}

export function theaterReset(apiUrl: string): Promise<TheaterState> {
  return postJson(`${apiUrl}/api/admin/demo/theater/reset`);
}

export function theaterBeat(apiUrl: string, beat: TheaterBeat): Promise<TheaterBeatResult> {
  return postJson(`${apiUrl}/api/admin/demo/theater/beat`, { beat });
}
